import { FeatureFlags } from '~/modules/feature_flags';
import { getUserLanguage } from '~/modules/helpers/languageHelper';
import { handleException } from '~/modules/utilities/error_utilities';
import { ENGLISH_LANGUAGE_CODE } from '~/modules/utilities/language_utils';

export const SLACK_PILOT_KEY = 'slackPilot';

const ENROLL_VALUE = 'true';
const UNENROLL_VALUE = 'false';
let slackPilotEnrolled = false;

function readEnrollment(): boolean {
    try {
        return localStorage.getItem(SLACK_PILOT_KEY) === ENROLL_VALUE;
    } catch {
        return false;
    }
}

/**
 * Runs before boot_client's dynamic import (see index.ts), so an invite-link enrollment is available
 * to every search consumer on the first render. Enrollment is a startup URL flow rather than a live,
 * in-page preference control. Pilot enrollment is English-only on AppExchange and AgentExchange;
 * Japanese experiences remain excluded.
 */
export function applyEnrollmentFromUrl(): void {
    const url = new URL(window.location.href);
    const value = url.searchParams.get(SLACK_PILOT_KEY)?.toLowerCase();
    if (value !== ENROLL_VALUE && value !== UNENROLL_VALUE) {
        slackPilotEnrolled = readEnrollment();
        return;
    }

    if (value === ENROLL_VALUE && getUserLanguage() !== ENGLISH_LANGUAGE_CODE) {
        slackPilotEnrolled = readEnrollment();
        try {
            url.searchParams.delete(SLACK_PILOT_KEY);
            window.history.replaceState({}, '', url.toString());
        } catch (error) {
            handleException(error, 'slackPilot.applyEnrollmentFromUrl.stripEnrollmentParameter', { level: 'warn' });
        }
        return;
    }

    try {
        if (value === ENROLL_VALUE) {
            localStorage.setItem(SLACK_PILOT_KEY, ENROLL_VALUE);
            slackPilotEnrolled = true;
        } else {
            localStorage.removeItem(SLACK_PILOT_KEY);
            slackPilotEnrolled = false;
        }
    } catch (error) {
        slackPilotEnrolled = readEnrollment();
        // Use the current structured, warn-level path; orgPickerPilot retains the older logger convention.
        handleException(error, 'slackPilot.applyEnrollmentFromUrl.persistEnrollment', { level: 'warn' });
        // Retain the one-shot parameter so a later reload can retry enrollment, matching orgPickerPilot.
        return;
    }

    try {
        url.searchParams.delete(SLACK_PILOT_KEY);
        window.history.replaceState({}, '', url.toString());
    } catch (error) {
        handleException(error, 'slackPilot.applyEnrollmentFromUrl.stripEnrollmentParameter', { level: 'warn' });
    }
}

function isSlackPilotEnabled(): boolean {
    return getUserLanguage() === ENGLISH_LANGUAGE_CODE && slackPilotEnrolled;
}

/**
 * The Slack Pilot intentionally enables the combined Slack listings and consistent-filters experience
 * for one cohort. Public flags remain independent rollout controls; only the pilot override is English-only.
 * Enrollment is initialized once before client boot to avoid reading storage from repeated render getters.
 */
export function isSlackListingsEnabled(): boolean {
    return FeatureFlags.FF_SLACK_MARKETPLACE_INTEGRATION || isSlackPilotEnabled();
}

// TODO(W-23547463): Remove this bridge when the Slack Pilot retires.
export function isConsistentFiltersEnabled(): boolean {
    return FeatureFlags.FF_CONSISTENT_FILTERS || isSlackPilotEnabled();
}
